import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

const businessHoursData = [
  { dayKey: 'day_monday', hours: 'Closed' },
  { dayKey: 'day_tuesday', hours: '11:00 AM – 11:00 PM' },
  { dayKey: 'day_wednesday', hours: '11:00 AM – 11:00 PM' },
  { dayKey: 'day_thursday', hours: '11:00 AM – 11:00 PM' },
  { dayKey: 'day_friday', hours: '11:00 AM – 11:00 PM' },
  { dayKey: 'day_saturday', hours: '11:00 AM – 11:00 PM' },
  { dayKey: 'day_sunday', hours: '11:00 AM – 11:00 PM' },
];

const todayIndex = new Date().getDay(); // 0=Sun, 1=Mon...
// Map JS day index to our array index (Mon=0 in our array, Sun=6)
const todayArrayIndex = todayIndex === 0 ? 6 : todayIndex - 1;

export default function LocationSection() {
  const { t } = useTranslation();

  return (
    <section id="location" className="bg-black py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#F57F25] text-xs font-bold tracking-widest uppercase mb-4">
            <div className="w-8 h-px bg-[#F57F25]"></div>
            {t('location_eyebrow')}
            <div className="w-8 h-px bg-[#F57F25]"></div>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight mb-4">
            {t('location_headline')} <span className="text-[#F57F25]">{t('location_headline_accent')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('location_sub')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Contact Card */}
          <div className="w-full lg:w-[45%] bg-[#111] rounded-2xl p-8 border border-white/5 flex flex-col gap-6">
            {/* Business Name */}
            <div>
              <h3 className="text-[#F57F25] font-black text-2xl uppercase tracking-wide">{t('location_business_name')}</h3>
              <p className="text-white/50 text-sm mt-1">{t('location_business_sub')}</p>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F57F25]/10 rounded-full shrink-0 mt-0.5">
                <i className="ri-map-pin-line text-[#F57F25] text-lg"></i>
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{t('location_address_label')}</div>
                <div className="text-white/60 text-sm mt-0.5">Boise, Idaho 83701<br />United States</div>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F57F25]/10 rounded-full shrink-0">
                <i className="ri-phone-line text-[#F57F25] text-lg"></i>
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{t('location_phone_label')}</div>
                <a href="tel:+19862173604" className="text-[#F57F25] text-sm font-bold hover:underline cursor-pointer">
                  +1 (986) 217-3604
                </a>
              </div>
            </div>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-base py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              {t('btn_order_whatsapp')}
            </a>

            {/* Business Hours */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-[#F57F25]"></i>
                </div>
                <span className="text-white font-bold text-sm uppercase tracking-wide">{t('location_hours_label')}</span>
              </div>
              <div className="space-y-2">
                {businessHoursData.map((h, idx) => {
                  const isToday = idx === todayArrayIndex;
                  const isClosed = h.hours === 'Closed';
                  return (
                    <div
                      key={h.dayKey}
                      className={`flex justify-between items-center py-1.5 px-3 rounded-lg text-sm ${
                        isToday ? 'bg-[#F57F25]/15 border border-[#F57F25]/30' : ''
                      }`}
                    >
                      <span className={`font-medium ${isToday ? 'text-[#F57F25]' : 'text-white/60'}`}>
                        {t(h.dayKey)}
                        {isToday && (
                          <span className="ml-2 text-xs bg-[#F57F25] text-black px-1.5 py-0.5 rounded-full">{t('location_today')}</span>
                        )}
                      </span>
                      <span className={`font-semibold ${isClosed ? 'text-red-400' : isToday ? 'text-white' : 'text-white/80'}`}>
                        {isClosed ? t('location_closed') : h.hours}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Social Media */}
            <div>
              <div className="text-white font-bold text-sm uppercase tracking-wide mb-3">{t('location_follow_label')}</div>
              <div className="flex gap-3">
                <a
                  href="https://instagram.com/bucara__burger"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-[#F57F25]/10 hover:bg-[#F57F25] rounded-full transition-all duration-200 cursor-pointer group"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-[#F57F25] group-hover:text-black text-xl"></i>
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61589000696717"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-[#F57F25]/10 hover:bg-[#F57F25] rounded-full transition-all duration-200 cursor-pointer group"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-line text-[#F57F25] group-hover:text-black text-xl"></i>
                </a>
                <a
                  href="https://tiktok.com/@bucara__burger"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-[#F57F25]/10 hover:bg-[#F57F25] rounded-full transition-all duration-200 cursor-pointer group"
                  aria-label="TikTok"
                >
                  <i className="ri-tiktok-line text-[#F57F25] group-hover:text-black text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="w-full lg:w-[55%] rounded-2xl overflow-hidden border border-white/5 min-h-[400px] lg:min-h-0">
            <iframe
              title="Bucara Burger Location - Boise Idaho"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95000!2d-116.2023!3d43.6150!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54aef172b942d3b9%3A0x9a5b9e1e3e3e3e3e!2sBoise%2C%20ID!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '450px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-12 bg-[#F57F25] rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-black font-black text-3xl md:text-4xl uppercase tracking-tight mb-3">
            {t('location_cta_title')}
          </h3>
          <p className="text-black/70 text-base mb-6">
            {t('location_cta_sub')}
          </p>
          <a
            href="https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-black hover:bg-black/80 text-white font-bold text-base px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            {t('location_cta_btn')}
          </a>
        </div>
      </div>
    </section>
  );
}
